import type { JSX } from 'react';
import { toast } from '@/lib/toast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type BreadcrumbItem, useBreadcrumbEffect } from '@/components/blocks/chrome';
import { LoadingState, NotFoundState } from '@/components';
import { PageContent, PageHeader, PageLayout } from '@/components/blocks/page';
import { useParams as useRouteParams } from '@tanstack/react-router';
import { useReport, useReportLock } from '../../hooks';
import { addRecentItem } from '@/hooks/useRecentItems';
import {
  useCompany,
  useSite,
  useUpdateRefrigerationUnit,
  useReportActions,
} from '../../../companies/hooks';
import type { CompanyWithSites } from '@/data/repos/companiesRepo';
import { useSettings } from '../../../settings/hooks/useSettings';
import { createReportFromTemplate } from '@/data/reports/createReportFromTemplate';
import { applyScorecardHistory } from '@/data/reports/scorecardHistory';
import { SCORECARD_HISTORY_POINTS } from '@/data/scorecardConfig';
import { queryKeys } from '@/lib/query-keys';
import {
  augmentReportForRendering,
  buildDraftContext,
} from '../../utils/augmentReportForRendering';
import { useUnifiedPDFExport } from '../../hooks/useUnifiedPDFExport';
import { NewReportModal, type NewReportModalMode } from './NewReportModal';
import { useEntityDevLogger } from '@/hooks/useEntityDevLogger';
import { useEntityModal } from '@/features/reports/hooks/useEntityModal';
import { ReportPersonnelModal } from '@/features/reports/components/ReportPersonnelModal';
import { ReportPersonnelProvider } from '@/features/reports/contexts/ReportPersonnelContext';
import { clearReportDraft, setReportDraftValue } from '@/features/reports/state/reportDraftStore';
import { useSidebar } from '@/components/ui/sidebar';
import type { Report, Company, Site, ReportView, RefrigerationUnit } from '@/data/schemas';
import type { RefrigerationUnitUpdateInput } from '@/data/repos/refrigerationUnitsRepo';
import { formatVisitControlPeriod, computeNextVisitDate } from '@/utils/periodicity';
import { toIsoMonth } from '@/utils/date';
import {
  SCHEDULING_GRACE_DAYS,
  applyDateToDateTime,
  deriveScheduleState,
  getReportScheduleWarnings,
  resolvePlannedVisitAt,
  resolveReportAt,
  shouldConsumePlannedVisit,
} from '@/utils/inspectionScheduling';
import { parseKeypath } from '@/data/idGenerator';
import { VariableResolver } from '@/data/variableResolver';
import { DEFAULT_SETTINGS } from '@/data/settings/defaultSettings';
import { useModal } from '@/stores/ui-store';
import { useAuth } from '@/features/auth/AuthProvider';
import { fetchReportAttachment, getDeviceId } from '@/data/sync';
import { useDataProvider } from '@/data/provider';
import { useWorkingSetStatus } from '@/hooks/useWorkingSetStatus';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { findMissingRequiredFields } from '@/features/reports/utils/reportFinalization';
import type { EntityFieldDescriptor } from '@/features/reports/components/fields/FieldRenderer';
import type { SupportedEntity } from '@/features/reports/utils/fieldSourceParser';
import { EntityFieldModal } from '@/features/reports/components/EntityFieldModal';
import { EditCompanyModal } from '@/features/companies/screens/CompanyDetail/EditCompanyModal';
import { SiteModal } from '@/features/companies/screens/CompanyDetail/SiteModal';
import { RefrigerationUnitModal } from '@/features/companies/screens/RefrigerationUnitDetail/RefrigerationUnitModal';
import {
  buildCompanyUpdatePayload,
  buildSiteUpdatePayload,
  buildUnitUpdatePayload,
  getEntityDisplayInfo,
  resolveEntityFieldTargets,
  type FocusedEntityResolution,
} from '@/features/reports/utils/entityFieldMappings';
import { ReportDetailContext, type ReportDetailContextValue } from './ReportDetailContext';
import type {
  FieldSourceMeta,
  PendingChanges,
  ReportCurrentRoute,
  ReportNavigation,
} from './types';
import { DOCUMENT_ROUTE } from './constants';
import {
  buildNavigation,
  deriveRouteFromLocation,
  isSameRoute,
} from './utils/reportDetailNavigation';
import { collectFieldSourceMeta } from './utils/reportDetailSections';
import { mergeSectionComments, type SectionCommentUpdates } from '../../utils/sectionComments';
import type { ReportSectionComment } from '@/data/schemas';
import { ReportDetailScaffold } from './components/ReportDetailScaffold';
import { Button } from '@/components/ui/button';
import { trackReportFinalized } from '@/lib/metrics';
import {
  logReportFinalized,
  logEntityDeleted,
  logEntityCreated,
} from '@/data/services/auditService';

const VISIT_SECTION_ID = 'sec_quadro-riepilogativo-dellispezione';
const VISIT_DATE_FIELD_ID = 'fld_data-sopralluogo';
const VISIT_PERIOD_FIELD_ID = 'fld_periodo-di-controllo';
const VISIT_DATE_KEYPATH = `${VISIT_SECTION_ID}.${VISIT_DATE_FIELD_ID}`;
const VISIT_PERIOD_KEYPATH = `${VISIT_SECTION_ID}.${VISIT_PERIOD_FIELD_ID}`;
const VISIT_ATTENDEES_SECTION_ID = 'sec_persone-presenti-al-sopralluogo';
const VISIT_ATTENDEES_FIELD_ID = 'fld_partecipanti-sopralluogo';

type FocusedEntityModalState = Extract<FocusedEntityResolution, { type: 'focused' }> & {
  unitIndex?: number;
  unitId?: string;
};

function buildNavigationSignature(view: ReportView): string {
  return JSON.stringify(view.toc);
}

function collectAttachmentUrls(
  comments: Record<string, ReportSectionComment | null | undefined>
): string[] {
  const paths = new Set<string>();
  Object.values(comments).forEach((comment) => {
    comment?.media?.forEach((media) => {
      if (media.storagePath) {
        paths.add(media.storagePath);
      }
    });
  });
  return Array.from(paths);
}

export function ReportDetailLayout(): JSX.Element {
  const { reportId } = useRouteParams({ from: '/reports/$reportId' });
  const isNewReport = reportId === 'new';
  const { data: workingSetStatus } = useWorkingSetStatus();
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, setOpen } = useSidebar();
  const sidebarCollapsed = state === 'collapsed';
  const { open: openModal } = useModal();
  const previousSidebarCollapsedRef = useRef(sidebarCollapsed);

  useEffect(() => {
    const previousSidebarCollapsed = previousSidebarCollapsedRef.current;
    setOpen(false);
    return () => {
      setOpen(!previousSidebarCollapsed);
    };
  }, [setOpen]);

  useEffect(() => {
    if (!sidebarCollapsed) {
      setOpen(false);
    }
  }, [sidebarCollapsed, setOpen]);

  const search = useSearch({ from: '/reports/$reportId' });
  const requestedEditMode = search.mode === 'edit';
  const searchCompanyId = search.companyId;
  const searchSiteId = search.siteId;

  const { data: report, isLoading } = useReport(isNewReport ? undefined : reportId);
  const shouldBlockOffline = !isOnline && !workingSetStatus.isReady;
  const { data: lockState } = useReportLock(isNewReport ? undefined : reportId);
  const { user } = useAuth();
  const { companies, sites, reports, templates, units, catalogs } = useDataProvider();

  const resolvedCompanyId = isNewReport ? searchCompanyId : report?.companyId;
  const resolvedSiteId = isNewReport ? undefined : report?.siteId;

  const { data: company, isLoading: isCompanyLoading } = useCompany(resolvedCompanyId);
  const { data: site } = useSite(resolvedSiteId);
  const updateUnitMutation = useUpdateRefrigerationUnit(report?.companyId, report?.siteId);
  const { data: settings } = useSettings();
  const { data: actions = [] } = useReportActions(isNewReport ? undefined : reportId);
  const { data: latestFinalizedReport } = useQuery({
    queryKey: site?.id
      ? queryKeys.reports.latestFinalizedBySite(site.id)
      : [...queryKeys.reports.all, 'latest-finalized', 'none'],
    queryFn: () => (site?.id ? reports.getLatestFinalizedBySite(site.id) : null),
    enabled: Boolean(site?.id),
  });

  const templateQuery = useQuery({
    queryKey:
      report?.templateId && report?.templateVersion
        ? queryKeys.templates.version(report.templateId, report.templateVersion)
        : queryKeys.templates.default(),
    queryFn: async () => {
      if (!report?.templateId || !report?.templateVersion) return null;
      return templates.getVersion(report.templateId, report.templateVersion);
    },
    enabled: Boolean(report?.templateId && report?.templateVersion),
  });

  const template = templateQuery.data ?? null;
  const isDraft = report?.status === 'draft';
  const {
    modal: entityModal,
    openCompanyModal,
    openSiteModal,
    openUnitModal,
    openPersonnelModal,
    closeModal: closeEntityModal,
  } = useEntityModal();
  const [isEditMode, setIsEditMode] = useState(false);
  const [focusedEntityModal, setFocusedEntityModal] = useState<FocusedEntityModalState | null>(
    null
  );
  const [isFocusedEntitySaving, setIsFocusedEntitySaving] = useState(false);
  const lockOwner = useMemo(
    () => ({
      deviceId: getDeviceId(),
      label: user?.email ?? user?.id ?? undefined,
    }),
    [user?.email, user?.id]
  );
  const isLockOwner =
    lockState?.lockedDevice === lockOwner.deviceId ||
    (lockOwner.label && lockState?.lockedBy === lockOwner.label);
  const lockOwnerLabel =
    lockState?.lockedBy ??
    (lockState?.lockedDevice
      ? `Dispositivo ${lockState.lockedDevice.slice(0, 6)}`
      : 'Altro utente');
  const lockMessage =
    lockState?.isLocked && !isLockOwner ? `Report bloccato da ${lockOwnerLabel}.` : null;

  const isInitialMount = useRef(true);
  const invalidateLock = useCallback(async () => {
    if (!report?.id) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.reports.lock(report.id),
    });
  }, [queryClient, report?.id]);

  const attemptEnterEditMode = useCallback(async (): Promise<boolean> => {
    if (!report?.id || !isDraft) return false;
    try {
      const result = await reports.acquireLock(report.id, lockOwner);
      await invalidateLock();

      if (result.status === 'locked') {
        toast.warning(`Report bloccato da ${lockOwnerLabel}.`);
        setIsEditMode(false);
        navigate({
          to: '/reports/$reportId',
          params: { reportId },
          search: { mode: undefined },
          replace: true,
        });
        return false;
      }

      setIsEditMode(true);
      return true;
    } catch {
      toast.error('Impossibile acquisire il blocco del report.');
      setIsEditMode(false);
      return false;
    }
  }, [invalidateLock, isDraft, lockOwner, lockOwnerLabel, navigate, report?.id, reportId, reports]);

  const releaseReportLock = useCallback(async () => {
    if (!report?.id) return;
    await reports.releaseLock(report.id, lockOwner);
    await invalidateLock();
  }, [invalidateLock, lockOwner, report?.id, reports]);
  useEffect(() => {
    if (!report) return;

    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    if (requestedEditMode && !isDraft) {
      toast.warning('I report finalizzati non sono modificabili');
      navigate({
        to: '/reports/$reportId',
        params: { reportId },
        search: { mode: undefined },
        replace: true,
      });
      setIsEditMode(false);
    } else if (requestedEditMode && isDraft) {
      void attemptEnterEditMode();
    }
  }, [attemptEnterEditMode, isDraft, navigate, report, reportId, requestedEditMode]);

  useEffect(() => {
    isInitialMount.current = true;
  }, [reportId]);

  useEffect(() => {
    if (!isDraft && isEditMode) {
      setIsEditMode(false);
      navigate({
        to: '/reports/$reportId',
        params: { reportId },
        search: (prev) => ({ ...prev, mode: undefined }),
        replace: true,
      });
    }
  }, [isDraft, isEditMode, navigate, reportId]);

  useEffect(() => {
    if (!isEditMode) return;
    if (lockState?.isLocked && !isLockOwner) {
      toast.warning(`Report bloccato da ${lockOwnerLabel}.`);
      setIsEditMode(false);
      navigate({
        to: '/reports/$reportId',
        params: { reportId },
        search: (prev) => ({ ...prev, mode: undefined }),
        replace: true,
      });
    }
  }, [isEditMode, isLockOwner, lockOwnerLabel, lockState?.isLocked, navigate, reportId]);

  useEffect(() => {
    if (!isEditMode || !report?.id) return;

    let cancelled = false;

    const heartbeat = async () => {
      if (cancelled) return;
      const refreshed = await reports.refreshLock(report.id, lockOwner);
      await invalidateLock();
      if (!refreshed) {
        toast.warning(`Blocco report perso: ${lockOwnerLabel}.`);
        setIsEditMode(false);
      }
    };

    const interval = window.setInterval(heartbeat, 5 * 60 * 1000);
    void heartbeat();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      void releaseReportLock();
    };
  }, [
    invalidateLock,
    isEditMode,
    lockOwner,
    lockOwnerLabel,
    releaseReportLock,
    report?.id,
    reports,
  ]);

  // Track visit in recent items
  useEffect(() => {
    if (report && !isNewReport) {
      addRecentItem({
        id: report.id,
        type: 'report',
        label: report.meta.title || `Report ${report.id.substring(0, 8)}`,
      });
    }
  }, [report, isNewReport]);

  useEffect(() => {
    if (!report?.id) return undefined;
    return () => {
      clearReportDraft(report.id);
    };
  }, [report?.id]);

  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({});
  const [pendingSectionComments, setPendingSectionComments] = useState<SectionCommentUpdates>({});
  const [viewChanges, setViewChanges] = useState<PendingChanges>({});
  const [viewSectionComments, setViewSectionComments] = useState<SectionCommentUpdates>({});
  const [effectiveReport, setEffectiveReport] = useState<Report | null>(null);
  const [view, setView] = useState<ReportView | null>(null);
  const [nav, setNav] = useState<ReportNavigation | null>(null);
  const [fieldSourceMeta, setFieldSourceMeta] = useState<Record<string, FieldSourceMeta>>({});
  const fieldSourceMetaRef = useRef<Record<string, FieldSourceMeta>>({});
  const pendingChangesRef = useRef<PendingChanges>({});
  const pendingSectionCommentsRef = useRef<SectionCommentUpdates>({});
  const viewChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [currentRoute, setCurrentRoute] = useState<ReportCurrentRoute>(() => ({
    topLevel: '',
  }));
  const [activeAnchor, setActiveAnchor] = useState<string | undefined>(undefined);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);

  const locationState = useRouterState({
    select: (state) => state.location,
  });

  const routerHash = useMemo(
    () => locationState.hash?.replace(/^#/, '') ?? '',
    [locationState.hash]
  );

  const prevSearchRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    fieldSourceMetaRef.current = fieldSourceMeta;
  }, [fieldSourceMeta]);

  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);

  useEffect(() => {
    pendingSectionCommentsRef.current = pendingSectionComments;
  }, [pendingSectionComments]);

  useEffect(() => {
    if (viewChangeTimeoutRef.current) {
      clearTimeout(viewChangeTimeoutRef.current);
      viewChangeTimeoutRef.current = null;
    }

    if (!report) {
      setViewChanges({});
      setViewSectionComments({});
      return;
    }

    const hasPending =
      Object.keys(pendingChanges).length > 0 || Object.keys(pendingSectionComments).length > 0;

    if (!hasPending) {
      setViewChanges({});
      setViewSectionComments({});
      return;
    }

    viewChangeTimeoutRef.current = setTimeout(() => {
      setViewChanges(pendingChanges);
      setViewSectionComments(pendingSectionComments);
      viewChangeTimeoutRef.current = null;
    }, 250);

    return () => {
      if (viewChangeTimeoutRef.current) {
        clearTimeout(viewChangeTimeoutRef.current);
        viewChangeTimeoutRef.current = null;
      }
    };
  }, [pendingChanges, pendingSectionComments, report]);

  useEffect(() => {
    if (!nav) return;

    const prev = prevSearchRef.current;
    const curr = locationState.search as Record<string, unknown>;
    const prevKeys = Object.keys(prev).filter((k) => k !== 'mode');
    const currKeys = Object.keys(curr).filter((k) => k !== 'mode');
    const isModeOnlyChange =
      prevKeys.length === currKeys.length &&
      prevKeys.every((k) => prev[k] === curr[k]) &&
      prev.mode !== curr.mode;

    prevSearchRef.current = curr;

    if (isModeOnlyChange && currentRoute.topLevel) {
      return;
    }

    const nextRoute = deriveRouteFromLocation(
      nav,
      locationState.pathname,
      locationState.search,
      routerHash
    );

    setCurrentRoute((prevRoute) => (isSameRoute(prevRoute, nextRoute) ? prevRoute : nextRoute));

    if (nextRoute.hash) {
      setActiveAnchor(nextRoute.hash);
      return;
    }

    const node = nav.nodes.find((entry) => entry.id === nextRoute.topLevel);
    if (!node) {
      setActiveAnchor(undefined);
      return;
    }

    const nextAnchor = node.sections[0]?.anchor;
    setActiveAnchor(nextAnchor ?? undefined);
  }, [currentRoute.topLevel, nav, locationState.pathname, locationState.search, routerHash]);

  useEffect(() => {
    if (!report) {
      setEffectiveReport(null);
      return;
    }

    const hasPending =
      Object.keys(viewChanges).length > 0 || Object.keys(viewSectionComments).length > 0;
    if (!hasPending) {
      setEffectiveReport(report);
      return;
    }

    const nextValues: Record<string, unknown> = {
      ...report.data.values,
      ...viewChanges,
    };

    const nextMeta = { ...report.meta };
    if (viewChanges[VISIT_DATE_KEYPATH] !== undefined) {
      const override = viewChanges[VISIT_DATE_KEYPATH];
      if (typeof override === 'string') {
        nextMeta.visitDate = override;
      }
    }

    const nextComments = mergeSectionComments(report.data.comments, viewSectionComments);

    setEffectiveReport({
      ...report,
      meta: nextMeta,
      data: {
        ...report.data,
        values: nextValues,
        comments: nextComments,
        attachmentUrls: collectAttachmentUrls(nextComments),
      },
    });
  }, [report, viewChanges, viewSectionComments]);

  useEffect(() => {
    let cancelled = false;
    async function loadView(target: Report | null) {
      if (!target) {
        setView(null);
        setFieldSourceMeta({});
        return;
      }

      try {
        const companyRecord = company ?? null;
        const siteRecord = site ?? null;
        const result = await augmentReportForRendering(
          target,
          companyRecord,
          siteRecord,
          settings ?? undefined,
          { template: template ?? undefined }
        );
        if (cancelled) return;
        setView(result.view);
        setFieldSourceMeta(collectFieldSourceMeta(result.view));
      } catch (err) {
        console.error('Failed to build report view', err);
        if (!cancelled) {
          setView(null);
          setFieldSourceMeta({});
        }
      }
    }

    void loadView(effectiveReport);

    return () => {
      cancelled = true;
    };
  }, [company, effectiveReport, settings, site, template]);

  // Load missing attachments in parallel to prevent race conditions
  // Uses Promise.all and a single state update instead of individual updates
  useEffect(() => {
    if (!view) return;
    let cancelled = false;

    const missing = view.sections.flatMap((section) =>
      (section.commentMedia ?? []).filter((media) => !media.dataUrl && media.storagePath)
    );

    if (missing.length === 0) return;

    const loadAllAttachments = async () => {
      // Load all attachments in parallel
      const results = await Promise.all(
        missing.map(async (media) => {
          const dataUrl = await fetchReportAttachment(media.storagePath ?? '');
          return { storagePath: media.storagePath, dataUrl };
        })
      );

      if (cancelled) return;

      // Build a map of storagePath -> dataUrl for fast lookup
      const dataUrlMap = new Map<string, string>();
      for (const { storagePath, dataUrl } of results) {
        if (storagePath && dataUrl) {
          dataUrlMap.set(storagePath, dataUrl);
        }
      }

      if (dataUrlMap.size === 0) return;

      // Single state update with all loaded attachments
      setView((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map((section) => ({
            ...section,
            commentMedia: section.commentMedia?.map((item) => {
              if (!item.storagePath) return item;
              const newDataUrl = dataUrlMap.get(item.storagePath);
              if (!newDataUrl) return item;
              return { ...item, dataUrl: newDataUrl };
            }),
          })),
        };
      });
    };

    void loadAllAttachments();

    return () => {
      cancelled = true;
    };
  }, [view]);

  const navSignature = useMemo(() => {
    if (!view) return null;
    return buildNavigationSignature(view);
  }, [view]);
  const navSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!view) {
      setNav(null);
      navSignatureRef.current = null;
      return;
    }

    if (!navSignature) {
      setNav(null);
      return;
    }

    if (navSignatureRef.current === navSignature) {
      return;
    }

    navSignatureRef.current = navSignature;
    const navigation = buildNavigation(view);
    setNav(navigation);
  }, [navSignature, view]);

  const saveMutation = useMutation({
    mutationFn: async ({ updatedReport }: { updatedReport: Report }) => {
      await reports.upsert(updatedReport);
      return updatedReport;
    },
    onSuccess: (updatedReport) => {
      queryClient.setQueryData(['reports', 'detail', reportId], updatedReport);
      setPendingChanges({});
      setPendingSectionComments({});
      clearReportDraft(updatedReport.id);
    },
    onError: (err) => {
      console.error('Failed to save report', err);
      toast.error('Errore nel salvataggio');
    },
  });

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const buildPendingReportSnapshot = useCallback((): Report | null => {
    if (!report) return null;
    const nextChanges = pendingChangesRef.current;
    const commentChanges = pendingSectionCommentsRef.current;

    const nextMeta = { ...report.meta };
    let nextReportAt = report.reportAt;
    if (nextChanges[VISIT_DATE_KEYPATH] !== undefined) {
      const override = nextChanges[VISIT_DATE_KEYPATH];
      if (typeof override === 'string') {
        nextMeta.visitDate = override;
        const merged = applyDateToDateTime(override, report.reportAt);
        if (merged) {
          nextReportAt = merged;
        }
      }
    }

    const nextComments = mergeSectionComments(report.data.comments, commentChanges);

    return {
      ...report,
      reportAt: nextReportAt,
      meta: nextMeta,
      data: {
        ...report.data,
        values: {
          ...report.data.values,
          ...nextChanges,
        },
        comments: nextComments,
        attachmentUrls: collectAttachmentUrls(nextComments),
      },
    };
  }, [report]);

  const schedulingWarning = useMemo(() => {
    if (!report || !site || !isDraft) return null;
    const periodicityMonths = site.periodicity ? parseInt(site.periodicity, 10) : undefined;
    const lastFinalizedReportAt = latestFinalizedReport
      ? resolveReportAt(latestFinalizedReport)
      : undefined;
    const sitePlannedVisitAt = resolvePlannedVisitAt(site);
    const schedule = deriveScheduleState({
      contractActivationDate: site.contractActivationDate,
      periodicityMonths,
      lastFinalizedReportAt,
      nextPlannedVisitAt: sitePlannedVisitAt,
      hasDraft: true,
    });

    return getReportScheduleWarnings({
      anchorDate: schedule.anchorDate,
      expectedWindowStart: schedule.expectedWindowStart,
      expectedWindowEnd: schedule.expectedWindowEnd,
      reportAt: report.reportAt,
      plannedVisitAt: report.plannedVisitAt,
      sitePlannedVisitAt,
      graceDays: SCHEDULING_GRACE_DAYS,
    });
  }, [isDraft, latestFinalizedReport, report, site]);

  const handleApplyPlannedToReport = useCallback(async () => {
    if (!report || !site || !isDraft) return;
    const plannedDateTime = report.plannedVisitAt ?? resolvePlannedVisitAt(site);
    if (!plannedDateTime) {
      toast.warning('Nessuna data pianificata disponibile');
      return;
    }
    const snapshot = buildPendingReportSnapshot();
    if (!snapshot) return;
    try {
      await saveMutation.mutateAsync({
        updatedReport: {
          ...snapshot,
          reportAt: plannedDateTime,
          updatedAt: new Date().toISOString(),
        },
      });
      // Force refetch to update schedulingWarning
      queryClient.invalidateQueries({
        queryKey: queryKeys.reports.detail(report.id),
      });
      toast.success('Data sopralluogo aggiornata');
    } catch {
      // Error already handled by mutation
    }
  }, [buildPendingReportSnapshot, isDraft, queryClient, report, saveMutation, site]);

  const handleApplyReportToPlanned = useCallback(async () => {
    if (!report || !site) return;
    const reportAtValue = resolveReportAt(report);
    if (!reportAtValue) {
      toast.warning('Data sopralluogo non disponibile');
      return;
    }
    try {
      await sites.updateSite(site.id, {
        periodicityNextVisitAt: reportAtValue,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sites.detail(site.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.companies.detail(site.companyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.planning.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
      toast.success('Pianificazione aggiornata');
    } catch (error) {
      console.error('Failed to update site planning:', error);
      toast.error('Impossibile aggiornare la pianificazione');
    }
  }, [queryClient, report, site, sites]);

  const scheduleSave = useCallback(
    (changes?: PendingChanges) => {
      if (!report || !isDraft) return;
      const nextChanges = changes ?? pendingChangesRef.current;
      const commentChanges = pendingSectionCommentsRef.current;

      const hasValueChanges = Object.keys(nextChanges).length > 0;
      const hasCommentChanges = Object.keys(commentChanges).length > 0;
      if (!hasValueChanges && !hasCommentChanges) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        const nextMeta = { ...report.meta };
        let nextReportAt = report.reportAt;
        if (nextChanges[VISIT_DATE_KEYPATH] !== undefined) {
          const override = nextChanges[VISIT_DATE_KEYPATH];
          if (typeof override === 'string') {
            nextMeta.visitDate = override;
            const merged = applyDateToDateTime(override, report.reportAt);
            if (merged) {
              nextReportAt = merged;
            }
          }
        }

        const nextComments = mergeSectionComments(report.data.comments, commentChanges);

        const updatedReport: Report = {
          ...report,
          reportAt: nextReportAt,
          meta: nextMeta,
          data: {
            ...report.data,
            values: {
              ...report.data.values,
              ...nextChanges,
            },
            comments: nextComments,
            attachmentUrls: collectAttachmentUrls(nextComments),
          },
          updatedAt: new Date().toISOString(),
        };

        saveTimeoutRef.current = null;
        saveMutation.mutate({ updatedReport });
      }, 500);
    },
    [isDraft, report, saveMutation]
  );

  const syncEntityFields = useCallback(
    ({
      entity,
      nextCompany,
      nextSite,
    }: {
      entity: SupportedEntity;
      nextCompany?: Company | CompanyWithSites | null;
      nextSite?: Site | null;
    }) => {
      if (!report || report.status !== 'draft') {
        return;
      }

      const entries = Object.entries(fieldSourceMetaRef.current).filter(
        ([, meta]) => meta.entity === entity
      );

      if (entries.length === 0) {
        return;
      }

      const companyRecord = nextCompany ?? company ?? null;
      const siteRecord = nextSite ?? site ?? null;

      const visitDate = report.meta.visitDate;
      const visitPeriodValue = report.data.values[VISIT_PERIOD_KEYPATH];
      const frequencyMonths = siteRecord?.periodicity
        ? parseInt(siteRecord.periodicity, 10)
        : undefined;
      const computedVisitPeriod =
        typeof visitPeriodValue === 'string' && visitPeriodValue.trim().length > 0
          ? visitPeriodValue
          : formatVisitControlPeriod(visitDate, frequencyMonths);
      const nostroRecapito = settings?.nostroRecapito ?? DEFAULT_SETTINGS.nostroRecapito;

      const context = buildDraftContext({
        report,
        company: companyRecord ?? undefined,
        site: siteRecord ?? undefined,
        visitDate,
        visitPeriod: computedVisitPeriod,
        frequencyMonths,
        nostroRecapito,
      });

      const resolver = new VariableResolver(context);
      const updates: Record<string, unknown> = {};

      for (const [keypath, meta] of entries) {
        try {
          const fieldType = (meta.fieldType ?? 'text') as Parameters<
            VariableResolver['resolveSource']
          >[1];
          const resolved = resolver.resolveSource(meta.source, fieldType);
          const currentValue = report.data.values[keypath];
          if (!Object.is(currentValue, resolved)) {
            updates[keypath] = resolved ?? '';
          }
        } catch {
          // Ignore resolution errors for derived fields.
        }
      }

      if (Object.keys(updates).length === 0) {
        return;
      }

      setPendingChanges((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const [key, value] of Object.entries(updates)) {
          if (key in prev && Object.is(prev[key], value)) {
            continue;
          }
          next[key] = value;
          changed = true;
        }
        if (!changed) {
          return prev;
        }
        scheduleSave(next);
        return next;
      });
    },
    [company, report, scheduleSave, settings, site]
  );

  const handleFieldChange = useCallback(
    (sectionId: string, _rowItemId: string | undefined, fieldId: string, value: unknown) => {
      if (!report || !isDraft) return;
      const keypath = `${sectionId}.${fieldId}`;
      const draftValue = value === undefined ? null : value;
      setReportDraftValue(report.id, keypath, draftValue);
      const updates: PendingChanges = { [keypath]: value };

      if (keypath === VISIT_DATE_KEYPATH) {
        const rawDate = typeof value === 'string' ? value.trim() : '';
        const frequencyMonths = site?.periodicity ? parseInt(site.periodicity, 10) : undefined;
        if (!rawDate) {
          updates[VISIT_PERIOD_KEYPATH] = '';
          setReportDraftValue(report.id, VISIT_PERIOD_KEYPATH, '');
        } else if (frequencyMonths) {
          const periodLabel = formatVisitControlPeriod(rawDate, frequencyMonths);
          if (periodLabel) {
            updates[VISIT_PERIOD_KEYPATH] = periodLabel;
            setReportDraftValue(report.id, VISIT_PERIOD_KEYPATH, periodLabel);
          }
        }
      }

      setPendingChanges((prev) => {
        const next = { ...prev, ...updates };
        scheduleSave(next);
        return next;
      });
    },
    [isDraft, report, scheduleSave, site]
  );

  const handleSectionCommentChange = useCallback(
    (sectionId: string, comment: Omit<ReportSectionComment, 'updatedAt'> | null) => {
      if (!report || !isDraft) return;
      const nextText = comment?.text?.trim() ?? '';
      const nextMedia = comment?.media ?? [];
      setPendingSectionComments((prev) => {
        const next = { ...prev };
        if (!nextText && nextMedia.length === 0) {
          next[sectionId] = null;
        } else {
          next[sectionId] = {
            text: nextText || undefined,
            media: nextMedia.length > 0 ? nextMedia : undefined,
            updatedAt: new Date().toISOString(),
          };
        }
        pendingSectionCommentsRef.current = next;
        scheduleSave(pendingChangesRef.current);
        return next;
      });
    },
    [isDraft, report, scheduleSave]
  );

  const updatePlanningAfterFinalization = useCallback(
    async (visitDate: string, reportAtValue?: string | null) => {
      try {
        if (!site?.id) return;
        const frequency = site.periodicity ?? site.frequency;
        const nextVisit = computeNextVisitDate(visitDate, frequency);
        const plannedPeriod = nextVisit ? toIsoMonth(nextVisit) : undefined;
        if (plannedPeriod) {
          const plannedUpdatedAt = new Date().toISOString();
          await units.updatePlanningForSite(site.id, {
            plannedPeriod,
            plannedDate: undefined,
            plannedSource: 'report_finalized',
            plannedUpdatedAt,
          });
        }
        const resolvedReportAt = resolveReportAt({
          reportAt: reportAtValue ?? undefined,
          meta: { visitDate },
        });
        const sitePlannedAt = resolvePlannedVisitAt(site);
        if (
          shouldConsumePlannedVisit({
            plannedVisitAt: sitePlannedAt,
            reportAt: resolvedReportAt,
          })
        ) {
          await sites.updateSite(site.id, {
            periodicityNextVisitAt: undefined,
            periodicityNextVisit: undefined,
          });
        }
        queryClient.invalidateQueries({
          queryKey: queryKeys.sites.detail(site.id),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.detail(site.companyId),
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.planning.list() });
        queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
      } catch (error) {
        console.error('Failed to update unit planning after finalization:', error);
      }
    },
    [queryClient, site, units, sites]
  );

  const handleFinalizeDraft = useCallback(async () => {
    if (!report || !template) {
      toast.error('Report o template non disponibili');
      return;
    }

    if (!isDraft) {
      toast.warning('Il report è già finalizzato');
      return;
    }

    if (isFinalizing) return;

    const snapshot = buildPendingReportSnapshot();
    if (!snapshot) return;

    const visitDate = snapshot.meta.visitDate;
    const visitPeriodValue = snapshot.data.values[VISIT_PERIOD_KEYPATH];
    const frequencyMonths = site?.periodicity ? parseInt(site.periodicity, 10) : undefined;
    const computedVisitPeriod =
      typeof visitPeriodValue === 'string' && visitPeriodValue.trim().length > 0
        ? visitPeriodValue
        : formatVisitControlPeriod(visitDate, frequencyMonths);
    const nostroRecapito = settings?.nostroRecapito ?? DEFAULT_SETTINGS.nostroRecapito;

    const context = buildDraftContext({
      report: snapshot,
      company: company ?? undefined,
      site: site ?? undefined,
      visitDate,
      visitPeriod: computedVisitPeriod,
      frequencyMonths,
      nostroRecapito,
    });

    const missing = findMissingRequiredFields({
      report: snapshot,
      template,
      context,
    });

    if (missing.length > 0) {
      toast.error(
        `Completa i campi obbligatori prima di finalizzare (${missing.length} mancanti).`
      );
      return;
    }

    setIsFinalizing(true);
    try {
      const finalizedAt = new Date().toISOString();
      const finalizedReport: Report = {
        ...snapshot,
        status: 'finalized',
        finalizedAt,
        updatedAt: finalizedAt,
      };

      await reports.upsert(finalizedReport);
      await updatePlanningAfterFinalization(
        finalizedReport.meta.visitDate,
        finalizedReport.reportAt
      );
      queryClient.setQueryData(queryKeys.reports.detail(finalizedReport.id), finalizedReport);
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      clearReportDraft(finalizedReport.id);
      setPendingChanges({});
      setPendingSectionComments({});
      setIsEditMode(false);
      navigate({
        to: DOCUMENT_ROUTE,
        params: { reportId: finalizedReport.id },
        search: (prev) => ({ ...prev, mode: undefined }),
        replace: true,
      });
      trackReportFinalized();
      // Audit log for report finalization (fire-and-forget)
      void logReportFinalized(finalizedReport.id, {
        companyId: finalizedReport.companyId,
        companyName: company?.name,
        siteId: finalizedReport.siteId,
        siteName: site?.name,
        visitDate: finalizedReport.meta.visitDate,
      });
      toast.success('Report finalizzato');
    } catch (error) {
      console.error('Failed to finalize report:', error);
      toast.error('Impossibile finalizzare il report');
    } finally {
      setIsFinalizing(false);
    }
  }, [
    buildPendingReportSnapshot,
    company,
    isDraft,
    isFinalizing,
    navigate,
    queryClient,
    report,
    reports,
    settings?.nostroRecapito,
    site,
    template,
    updatePlanningAfterFinalization,
  ]);

  const handleDeleteDraft = useCallback(() => {
    if (!report || !isDraft || isDeletingDraft) return;

    openModal('confirm-delete', {
      title: 'Elimina bozza',
      message: "Questa bozza verrà eliminata definitivamente. L'azione non può essere annullata.",
      confirmLabel: 'Elimina bozza',
      variant: 'danger',
      onConfirm: async () => {
        setIsDeletingDraft(true);
        try {
          await reports.delete(report.id);
          clearReportDraft(report.id);
          // Audit log for report deletion (fire-and-forget)
          void logEntityDeleted('report', report.id, {
            companyId: report.companyId,
            companyName: company?.name,
            siteId: report.siteId,
            siteName: site?.name,
            status: report.status,
          });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.reports.all,
          });
          toast.success('Bozza eliminata');
          navigate({ to: '/reports', search: { query: undefined } });
        } catch (error) {
          console.error('Failed to delete draft report:', error);
          toast.error('Impossibile eliminare la bozza');
        } finally {
          setIsDeletingDraft(false);
        }
      },
    });
  }, [
    company?.name,
    isDeletingDraft,
    isDraft,
    navigate,
    openModal,
    queryClient,
    report,
    reports,
    site?.name,
  ]);

  const handleEntityFieldRequest = useCallback(
    (descriptor: EntityFieldDescriptor) => {
      if (!report || !isDraft) {
        toast.warning('Solo i report in bozza possono modificare i dati anagrafici');
        return;
      }

      if (descriptor.source.entity === 'company' && report.companyId && !company) {
        toast.info('Caricamento dati azienda in corso');
        return;
      }

      let unitIndex: number | undefined;
      let targetUnit: RefrigerationUnit | null = null;

      if (descriptor.source.entity === 'unit') {
        if (!report.siteId) {
          toast.warning('Dati stabilimento non disponibili');
          return;
        }

        if (!site || !Array.isArray(site.refrigerationUnits)) {
          toast.warning('Dati degli impianti non disponibili');
          return;
        }

        const keypath =
          descriptor.keypath ??
          (descriptor.fieldId ? `${descriptor.sectionId}.${descriptor.fieldId}` : undefined);

        if (!keypath) {
          toast.error("Impossibile aprire l'impianto selezionato");
          return;
        }

        try {
          const parsed = parseKeypath(keypath);
          unitIndex = parsed.indices[0];
        } catch {
          unitIndex = undefined;
        }

        if (typeof unitIndex !== 'number') {
          toast.error("Impossibile determinare l'impianto da modificare");
          return;
        }

        targetUnit = site.refrigerationUnits[unitIndex] ?? null;
        if (!targetUnit) {
          toast.error('Impianto non trovato nello stabilimento');
          return;
        }
      }

      const resolution = resolveEntityFieldTargets({
        descriptor,
        company: company ?? null,
        site: site ?? null,
        unit: targetUnit,
      });

      if (resolution.type === 'personnel') {
        if (!report.companyId) {
          toast.warning("Collega prima un'azienda per modificare il personale");
          return;
        }

        const focusAlarmContact =
          descriptor.source.path === 'alarmReference.contact' ||
          descriptor.source.paths.includes('alarmReference.contact');
        openPersonnelModal(report.companyId, {
          focusAlarmContact,
        });
        return;
      }

      if (resolution.type === 'focused') {
        setFocusedEntityModal({
          ...resolution,
          unitIndex,
          unitId: targetUnit?.id,
        });
        return;
      }

      if (resolution.type === 'fallback') {
        switch (resolution.reason) {
          case 'missing-company':
            toast.warning('Nessuna azienda collegata al report');
            return;
          case 'missing-site':
            toast.warning('Dati stabilimento non disponibili');
            return;
          case 'missing-unit':
            toast.warning("Dati dell'impianto non disponibili");
            return;
          case 'component-index-out-of-range':
            toast.error('Componente non disponibile');
            return;
          default:
            break;
        }
      }

      switch (descriptor.source.entity) {
        case 'company': {
          if (report.companyId) {
            openCompanyModal(report.companyId);
          } else {
            toast.warning('Nessuna azienda collegata al report');
          }
          return;
        }
        case 'site': {
          if (!report.siteId) {
            toast.warning('Dati stabilimento non disponibili');
            return;
          }

          if (!site) {
            toast.info('Caricamento dati stabilimento in corso');
            return;
          }

          openSiteModal(report.siteId);
          return;
        }
        case 'unit': {
          if (!report.siteId) {
            toast.warning('Dati stabilimento non disponibili');
            return;
          }

          if (!site || !Array.isArray(site.refrigerationUnits)) {
            toast.warning('Dati degli impianti non disponibili');
            return;
          }

          if (!targetUnit || typeof unitIndex !== 'number') {
            toast.error("Impossibile determinare l'impianto da modificare");
            return;
          }

          openUnitModal(site.id, targetUnit.id, unitIndex);
          return;
        }
        case 'personnel': {
          if (!report.companyId) {
            toast.warning("Collega prima un'azienda per modificare il personale");
            return;
          }

          openPersonnelModal(report.companyId);
          return;
        }
        default:
          return;
      }
    },
    [
      company,
      isDraft,
      openCompanyModal,
      openPersonnelModal,
      openSiteModal,
      openUnitModal,
      report,
      site,
    ]
  );

  const openPersonnelManager = useCallback(() => {
    handleEntityFieldRequest({
      source: {
        entity: 'personnel',
        path: '',
        paths: [''],
        raw: '{{personnel}}',
      },
      sectionId: VISIT_ATTENDEES_SECTION_ID,
      fieldId: VISIT_ATTENDEES_FIELD_ID,
      label: 'Personale',
    });
  }, [handleEntityFieldRequest]);

  const handleSettingsFieldRequest = useCallback(() => {
    navigate({ to: '/settings' }).catch(() => {
      /* navigation errors handled globally */
    });
  }, [navigate]);

  const handleCompanyUpdated = useCallback(
    (nextCompany: CompanyWithSites | null) => {
      if (!report || report.status !== 'draft') {
        return;
      }
      syncEntityFields({ entity: 'company', nextCompany });
    },
    [report, syncEntityFields]
  );

  const handleSiteUpdated = useCallback(
    (nextSite: Site | null) => {
      if (!report || report.status !== 'draft') {
        return;
      }
      syncEntityFields({ entity: 'site', nextSite });
    },
    [report, syncEntityFields]
  );

  const activeUnit = useMemo(() => {
    if (entityModal.type !== 'unit' || !site?.refrigerationUnits) {
      return null;
    }

    if (entityModal.id) {
      const match = site.refrigerationUnits.find((unit) => unit.id === entityModal.id);
      if (match) {
        return match;
      }
    }

    if (typeof entityModal.unitIndex === 'number') {
      return site.refrigerationUnits[entityModal.unitIndex] ?? null;
    }

    return null;
  }, [entityModal, site]);

  const focusedUnit = useMemo(() => {
    if (!focusedEntityModal || focusedEntityModal.entity !== 'unit') {
      return null;
    }

    const units = site?.refrigerationUnits ?? [];
    if (focusedEntityModal.unitId) {
      return units.find((unit) => unit.id === focusedEntityModal.unitId) ?? null;
    }

    if (typeof focusedEntityModal.unitIndex === 'number') {
      return units[focusedEntityModal.unitIndex] ?? null;
    }

    return null;
  }, [focusedEntityModal, site]);

  const focusedEntityDisplay = useMemo(() => {
    if (!focusedEntityModal) return null;
    return getEntityDisplayInfo({
      entity: focusedEntityModal.entity,
      company: company ?? null,
      site: site ?? null,
      unit: focusedUnit ?? null,
    });
  }, [company, focusedEntityModal, focusedUnit, site]);

  const focusedEntityFields = useMemo(
    () => focusedEntityModal?.targets.map((target) => target.field) ?? [],
    [focusedEntityModal]
  );

  const handleUnitUpdated = useCallback(
    async (unitInput: RefrigerationUnitUpdateInput) => {
      if (!report || report.status !== 'draft' || !site) {
        return;
      }

      const unit = site.refrigerationUnits?.find((entry) => entry.id === unitInput.id);
      if (!unit || !site.id) {
        return;
      }

      try {
        await updateUnitMutation.mutateAsync(unitInput);
      } finally {
        const refreshedSite = await sites.getSite(site.id);
        if (refreshedSite) {
          syncEntityFields({ entity: 'unit', nextSite: refreshedSite });
        }
      }
    },
    [report, site, sites, syncEntityFields, updateUnitMutation]
  );

  const handleFocusedEntitySave = useCallback(
    async (values: Record<string, unknown>) => {
      if (!focusedEntityModal) return;

      setIsFocusedEntitySaving(true);
      try {
        if (focusedEntityModal.entity === 'company') {
          if (!company) {
            toast.error('Azienda non disponibile');
            return;
          }

          const updatedCompany = buildCompanyUpdatePayload({
            targets: focusedEntityModal.targets,
            values,
            company,
          });
          const { sites: ignored, ...companyPayload } = updatedCompany;

          await companies.saveCompany(companyPayload);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: queryKeys.companies.all,
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.companies.detail(company.id),
            }),
          ]);

          const refreshed = await companies.getCompany(company.id);
          toast.success('Azienda aggiornata');
          handleCompanyUpdated(refreshed ?? updatedCompany);
          return;
        }

        if (focusedEntityModal.entity === 'site') {
          if (!site) {
            toast.error('Stabilimento non disponibile');
            return;
          }

          const updates = buildSiteUpdatePayload({
            targets: focusedEntityModal.targets,
            values,
          });

          await sites.updateSite(site.id, updates);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: queryKeys.companies.all,
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.companies.detail(site.companyId),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.sites.detail(site.id),
            }),
          ]);

          const refreshed = await sites.getSite(site.id);
          toast.success('Stabilimento aggiornato');
          handleSiteUpdated(refreshed ?? site);
          return;
        }

        if (focusedEntityModal.entity === 'unit') {
          if (!focusedUnit) {
            toast.error('Impianto non disponibile');
            return;
          }

          const updateInput = buildUnitUpdatePayload({
            targets: focusedEntityModal.targets,
            values,
            unit: focusedUnit,
          }) as RefrigerationUnitUpdateInput;

          await handleUnitUpdated(updateInput);
          toast.success('Impianto aggiornato');
        }
      } finally {
        setIsFocusedEntitySaving(false);
      }
    },
    [
      company,
      companies,
      focusedEntityModal,
      focusedUnit,
      handleCompanyUpdated,
      handleSiteUpdated,
      handleUnitUpdated,
      queryClient,
      site,
      sites,
    ]
  );

  const handleFocusedEntityModalChange = useCallback((open: boolean) => {
    if (!open) {
      setFocusedEntityModal(null);
      setIsFocusedEntitySaving(false);
    }
  }, []);

  const { previewPdf, generatePdf } = useUnifiedPDFExport({
    report: effectiveReport,
    company: company ?? null,
    site: site ?? null,
    settings: settings ?? undefined,
    isDraft,
  });
  const [isPdfPending, setIsPdfPending] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [lastPdfAction, setLastPdfAction] = useState<'preview' | 'download' | null>(null);

  const handlePreviewPdf = useCallback(async () => {
    if (isPdfPending) return;
    setIsPdfPending(true);
    setPdfError(null);
    setLastPdfAction('preview');
    const toastId = toast.loading('Generazione PDF in corso...');
    try {
      const result = await previewPdf();
      toast.dismiss(toastId);
      if (result.status === 'downloaded_fallback') {
        toast.info('Popup bloccato dal browser. Il PDF è stato scaricato automaticamente.');
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('[ReportDetail] PDF preview failed:', error);
      setPdfError('Generazione PDF fallita');
    } finally {
      setIsPdfPending(false);
    }
  }, [isPdfPending, previewPdf]);

  const handleGeneratePdf = useCallback(async () => {
    if (isPdfPending) return;
    setIsPdfPending(true);
    setPdfError(null);
    setLastPdfAction('download');
    const toastId = toast.loading('Generazione PDF in corso...');
    try {
      await generatePdf();
      toast.dismiss(toastId);
      toast.success('PDF scaricato');
    } catch (error) {
      toast.dismiss(toastId);
      console.error('[ReportDetail] PDF export failed:', error);
      setPdfError('Generazione PDF fallita');
    } finally {
      setIsPdfPending(false);
    }
  }, [generatePdf, isPdfPending]);

  const handleRetryPdf = useCallback(() => {
    if (lastPdfAction === 'preview') {
      void handlePreviewPdf();
      return;
    }

    if (lastPdfAction === 'download') {
      void handleGeneratePdf();
    }
  }, [handleGeneratePdf, handlePreviewPdf, lastPdfAction]);

  const handleDismissPdfError = useCallback(() => {
    setPdfError(null);
  }, []);

  const [isNewReportModalOpen, setIsNewReportModalOpen] = useState(false);
  const [newReportModalMode, setNewReportModalMode] = useState<NewReportModalMode>('company');
  const [newReportModalCompanyId, setNewReportModalCompanyId] = useState<string | undefined>(
    undefined
  );
  const [newReportModalSiteId, setNewReportModalSiteId] = useState<string | undefined>(undefined);
  const [newReportError, setNewReportError] = useState<string | null>(null);
  const [isCreatingNewReport, setIsCreatingNewReport] = useState(false);
  const newReportKeyRef = useRef<string | null>(null);
  const isCreatingNewReportRef = useRef(false);

  const handleOpenNewModal = useCallback(() => {
    setNewReportModalMode('company');
    setNewReportModalCompanyId(report?.companyId ?? searchCompanyId);
    setNewReportModalSiteId(report?.siteId ?? searchSiteId);
    setIsNewReportModalOpen(true);
  }, [report?.companyId, report?.siteId, searchCompanyId, searchSiteId]);

  const handleCloseNewModal = useCallback(() => {
    if (isNewReport) {
      if (searchCompanyId && searchSiteId) {
        navigate({
          to: '/companies/$companyId/sites/$siteId',
          params: { companyId: searchCompanyId, siteId: searchSiteId },
          search: { tab: undefined },
        });
        return;
      }

      if (searchCompanyId) {
        navigate({
          to: '/companies/$companyId',
          params: { companyId: searchCompanyId },
          search: { tab: undefined },
        });
        return;
      }

      navigate({
        to: '/reports',
        search: { query: undefined },
      });
      return;
    }

    setIsNewReportModalOpen(false);
  }, [isNewReport, navigate, searchCompanyId, searchSiteId]);

  const createNewReport = useCallback(
    async (companyData: CompanyWithSites, siteData: Site) => {
      if (isCreatingNewReportRef.current) return;
      isCreatingNewReportRef.current = true;
      setIsCreatingNewReport(true);
      setNewReportError(null);

      try {
        const existingDraft = await reports.getDraftBySite(siteData.id);
        if (existingDraft) {
          toast.info('Esiste già una bozza per questo stabilimento');
          navigate({
            to: '/reports/$reportId',
            params: { reportId: existingDraft.id },
            search: { mode: 'edit' },
            replace: true,
          });
          return;
        }

        const template = await templates.getDefault();
        if (!template) {
          toast.error('Template predefinito non disponibile');
          setNewReportError('Template predefinito non disponibile.');
          return;
        }

        const historyReports = await reports.getRecentFinalizedBySite(
          siteData.id,
          SCORECARD_HISTORY_POINTS
        );
        const previousReport = historyReports[0] ?? null;
        const catalogData = await catalogs.getAllCatalogs();
        const report = createReportFromTemplate({
          template,
          company: companyData,
          site: siteData,
          catalogs: catalogData,
          variables: settings?.nostroRecapito
            ? { nostroRecapito: settings.nostroRecapito }
            : undefined,
          previousReport: previousReport ?? undefined,
        });
        applyScorecardHistory(report, historyReports);

        await reports.upsert(report);
        // Audit log for report creation (fire-and-forget)
        void logEntityCreated('report', report.id, {
          companyId: companyData.id,
          companyName: companyData.name,
          siteId: siteData.id,
          siteName: siteData.name,
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });

        navigate({
          to: '/reports/$reportId',
          params: { reportId: report.id },
          search: { mode: 'edit' },
          replace: true,
        });
      } catch (error) {
        console.error('Failed to create report:', error);
        toast.error('Errore nella creazione del report');
        setNewReportError('Errore nella creazione del report.');
      } finally {
        setIsCreatingNewReport(false);
        isCreatingNewReportRef.current = false;
      }
    },
    [catalogs, navigate, queryClient, reports, settings?.nostroRecapito, templates]
  );

  useEffect(() => {
    if (!isNewReport) {
      setNewReportError(null);
      setIsCreatingNewReport(false);
      setIsNewReportModalOpen(false);
      newReportKeyRef.current = null;
      isCreatingNewReportRef.current = false;
      return;
    }

    const key = `${searchCompanyId ?? 'none'}:${searchSiteId ?? 'none'}`;
    if (newReportKeyRef.current === key) return;

    setNewReportError(null);
    setIsNewReportModalOpen(false);

    if (!searchCompanyId) {
      setNewReportModalMode('company');
      setNewReportModalCompanyId(undefined);
      setNewReportModalSiteId(undefined);
      setIsNewReportModalOpen(true);
      newReportKeyRef.current = key;
      return;
    }

    if (isCompanyLoading) return;

    if (!company) {
      toast.error('Azienda non valida o non disponibile.');
      setNewReportError('Azienda non valida o non disponibile.');
      newReportKeyRef.current = key;
      return;
    }

    if (company.sites.length === 0) {
      toast.error('Nessuno stabilimento disponibile per creare un report.');
      setNewReportError('Nessuno stabilimento disponibile per creare un report.');
      newReportKeyRef.current = key;
      return;
    }

    if (searchSiteId) {
      const matchedSite = company.sites.find((siteItem) => siteItem.id === searchSiteId);
      if (!matchedSite) {
        toast.error('Stabilimento non valido o non disponibile.');
        setNewReportError('Stabilimento non valido o non disponibile.');
        newReportKeyRef.current = key;
        return;
      }

      newReportKeyRef.current = key;
      createNewReport(company, matchedSite);
      return;
    }

    if (company.sites.length === 1) {
      newReportKeyRef.current = key;
      createNewReport(company, company.sites[0]);
      return;
    }

    setNewReportModalMode('site');
    setNewReportModalCompanyId(company.id);
    setNewReportModalSiteId(undefined);
    setIsNewReportModalOpen(true);
    newReportKeyRef.current = key;
  }, [company, createNewReport, isCompanyLoading, isNewReport, searchCompanyId, searchSiteId]);

  const devPayload = useMemo(() => {
    if (!report) return null;
    return {
      report,
      effectiveReport,
      view,
      nav,
      currentRoute,
    };
  }, [report, effectiveReport, view, nav, currentRoute]);

  useEntityDevLogger('Report detail', devPayload, { mode: 'initial' });

  const breadcrumbFactory = useCallback(
    (base: BreadcrumbItem[]) => {
      const trail: BreadcrumbItem[] = [...base, { label: 'Reportistica', href: '/reports' }];

      if (!report) {
        trail.push({ label: 'Report' });
        return trail;
      }

      trail.push({ label: report.meta.title });

      const currentNode = nav?.nodes.find((node) => node.id === currentRoute.topLevel);
      if (currentNode) {
        trail.push({ label: currentNode.label });
        if (currentRoute.hash) {
          const sectionLabel = currentNode.sections.find((section) =>
            section.sectionIds.includes(currentRoute.hash ?? '')
          )?.label;
          if (sectionLabel) {
            trail.push({ label: sectionLabel });
          }
        }
      }

      return trail;
    },
    [currentRoute.hash, currentRoute.topLevel, nav, report]
  );

  useBreadcrumbEffect(breadcrumbFactory);

  const updateReportMode = useCallback(
    (nextMode: boolean) => {
      const nextActiveAnchor = activeAnchor ?? currentRoute.hash;
      navigate({
        to: DOCUMENT_ROUTE,
        params: { reportId },
        search: (prev) => {
          const prevSearch = prev as Record<string, unknown>;
          const merged: Record<string, unknown> = {
            ...prevSearch,
            mode: nextMode ? 'edit' : undefined,
          };

          const nextSection =
            currentRoute.topLevel ||
            (typeof prevSearch.section === 'string' ? (prevSearch.section as string) : undefined);
          merged.section = nextSection;

          const nextSubsection =
            nextActiveAnchor ??
            (typeof prevSearch.subsection === 'string'
              ? (prevSearch.subsection as string)
              : undefined);
          merged.subsection = nextSubsection;

          if (!merged.section) {
            merged.section = undefined;
          }

          if (!nextSubsection) {
            merged.subsection = undefined;
          }

          return merged;
        },
        replace: true,
      });
    },
    [activeAnchor, currentRoute.hash, currentRoute.topLevel, navigate, reportId]
  );

  const contextValue = useMemo<ReportDetailContextValue | null>(() => {
    if (!report || !effectiveReport || !view || !nav) return null;
    return {
      report,
      effectiveReport,
      template,
      company: company ?? null,
      site: site ?? null,
      view,
      nav,
      isDraft,
      isEditMode,
      schedulingWarning: schedulingWarning ?? undefined,
      onApplyPlannedToReport: handleApplyPlannedToReport,
      onApplyReportToPlanned: handleApplyReportToPlanned,
      onToggleEdit: () => {
        if (!isDraft) return;
        if (isEditMode) {
          setIsEditMode(false);
          void releaseReportLock();
          updateReportMode(false);
          return;
        }

        void (async () => {
          const acquired = await attemptEnterEditMode();
          if (acquired) {
            updateReportMode(true);
          }
        })();
      },
      onFinalizeDraft: handleFinalizeDraft,
      onDeleteDraft: handleDeleteDraft,
      isFinalizing,
      isDeletingDraft,
      onOpenNewModal: handleOpenNewModal,
      onCloseNewModal: handleCloseNewModal,
      isNewModalOpen: isNewReportModalOpen,
      isPdfLoading: isPdfPending,
      pdfError,
      onPreviewPdf: handlePreviewPdf,
      onGeneratePdf: handleGeneratePdf,
      onRetryPdf: handleRetryPdf,
      onDismissPdfError: handleDismissPdfError,
      onSaveSection: () => saveMutation.reset(),
      onFieldChange: handleFieldChange,
      onSectionCommentChange: handleSectionCommentChange,
      onEntityFieldRequest: handleEntityFieldRequest,
      onSettingsFieldRequest: handleSettingsFieldRequest,
      currentSection: currentRoute,
      activeAnchor,
      setActiveAnchor,
      actions,
      lockState,
      lockMessage,
    };
  }, [
    actions,
    company,
    currentRoute,
    effectiveReport,
    handleDeleteDraft,
    handleFinalizeDraft,
    handleFieldChange,
    handleSectionCommentChange,
    handleEntityFieldRequest,
    handleSettingsFieldRequest,
    handleCloseNewModal,
    handleGeneratePdf,
    handleDismissPdfError,
    handlePreviewPdf,
    handleOpenNewModal,
    handleApplyPlannedToReport,
    handleApplyReportToPlanned,
    isDraft,
    isDeletingDraft,
    isEditMode,
    isFinalizing,
    isNewReportModalOpen,
    isPdfPending,
    pdfError,
    lockMessage,
    lockState,
    nav,
    releaseReportLock,
    updateReportMode,
    attemptEnterEditMode,
    report,
    saveMutation,
    schedulingWarning,
    site,
    template,
    view,
    activeAnchor,
    handleRetryPdf,
  ]);

  const personnelContextValue = useMemo(
    () => ({
      companyId: report?.companyId ?? null,
      companyName: company?.name ?? null,
      siteId: report?.siteId ?? null,
      openPersonnelManager,
    }),
    [report?.companyId, report?.siteId, company?.name, openPersonnelManager]
  );

  if (shouldBlockOffline) {
    return (
      <PageLayout className="px-6 py-6">
        <PageHeader title="Working set non disponibile" icon="offline_pin" />
        <PageContent>
          <div className="rounded-lg border border-[var(--border-default)] bg-surface p-6 shadow-sm">
            <p className="text-sm text-text-primary">
              Questo dispositivo non ha ancora scaricato i dati offline necessari per creare o
              modificare report.
            </p>
            <p className="mt-2 text-xs text-text-secondary">
              Connettiti a Internet almeno una volta e aggiorna il working set dalle impostazioni.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate({ to: '/settings/data' })}>
                Apri impostazioni dati
              </Button>
            </div>
          </div>
        </PageContent>
      </PageLayout>
    );
  }

  if (isNewReport) {
    if (newReportError) {
      return (
        <NotFoundState
          icon="error"
          title="Impossibile creare il report"
          description={newReportError}
        />
      );
    }

    if (isCreatingNewReport || (searchCompanyId && isCompanyLoading)) {
      return <LoadingState label="Creazione report" />;
    }

    return (
      <NewReportModal
        isOpen={isNewReportModalOpen}
        onClose={handleCloseNewModal}
        mode={newReportModalMode}
        lockedCompanyId={newReportModalMode === 'site' ? newReportModalCompanyId : undefined}
        preselectedCompanyId={
          newReportModalMode === 'company' ? newReportModalCompanyId : undefined
        }
        preselectedSiteId={newReportModalSiteId}
      />
    );
  }

  if (isLoading || !contextValue) {
    return <LoadingState label="Caricamento report" />;
  }

  if (!report) {
    return <></>;
  }

  return (
    <ReportDetailContext.Provider value={contextValue}>
      <ReportPersonnelProvider value={personnelContextValue}>
        <ReportDetailScaffold>
          <Outlet />
        </ReportDetailScaffold>
        {focusedEntityModal ? (
          <EntityFieldModal
            open={Boolean(focusedEntityModal)}
            onOpenChange={handleFocusedEntityModalChange}
            title={focusedEntityDisplay?.title ?? 'Modifica'}
            contextMessage={focusedEntityDisplay?.context ?? ''}
            fields={focusedEntityFields}
            schema={focusedEntityModal.schema}
            defaultValues={focusedEntityModal.defaultValues}
            onSave={handleFocusedEntitySave}
            isSaving={isFocusedEntitySaving}
          />
        ) : null}
        <EditCompanyModal
          open={entityModal.type === 'company'}
          onOpenChange={(open) => {
            if (!open) {
              closeEntityModal();
            }
          }}
          company={company ?? null}
          onUpdated={handleCompanyUpdated}
        />
        <SiteModal
          site={site ?? null}
          open={entityModal.type === 'site'}
          onOpenChange={(open) => {
            if (!open) {
              closeEntityModal();
            }
          }}
          onUpdated={handleSiteUpdated}
        />
        <RefrigerationUnitModal
          open={entityModal.type === 'unit' && Boolean(activeUnit)}
          onOpenChange={(open) => {
            if (!open) {
              closeEntityModal();
            }
          }}
          mode="edit"
          refrigerationUnit={activeUnit ?? undefined}
          onUpdate={activeUnit ? handleUnitUpdated : undefined}
          isSubmitting={updateUnitMutation.isPending}
        />
        <ReportPersonnelModal
          open={entityModal.type === 'personnel'}
          onOpenChange={(open) => {
            if (!open) {
              closeEntityModal();
            }
          }}
          companyId={report.companyId ?? null}
          companyName={company?.name ?? null}
          onCompanyUpdated={handleCompanyUpdated}
          focusAlarmContact={entityModal.personnelFocus === 'alarmContact'}
        />
        <NewReportModal
          isOpen={isNewReportModalOpen}
          onClose={handleCloseNewModal}
          mode={newReportModalMode}
          lockedCompanyId={newReportModalMode === 'site' ? newReportModalCompanyId : undefined}
          preselectedCompanyId={
            newReportModalMode === 'company' ? newReportModalCompanyId : undefined
          }
          preselectedSiteId={newReportModalSiteId}
        />
      </ReportPersonnelProvider>
    </ReportDetailContext.Provider>
  );
}
